import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type { ReleaseListItem } from './ReleaseList';

/** One choice in a release filter dropdown. */
export interface ReleaseFilterOption {
  value: string;
  label: string;
}

/** The org-wide feed: GET /releases. */
export interface ReleaseFeedResponse {
  releases: ReleaseListItem[];
  /** Matches before the limit was applied, for "showing 10 of 23". */
  totalCount: number;
  /** Largest limit the backend will honour. */
  maxLimit: number;
  filters: { repos: ReleaseFilterOption[]; owners: ReleaseFilterOption[] };
  repoCount: number;
  failedRepoCount: number;
  fetchedAt: string;
}

/** One repository's releases: GET /releases/:owner/:repo. */
export interface RepoReleasesResponse {
  releases: ReleaseListItem[];
  totalCount: number;
  maxLimit: number;
  fetchedAt: string;
}

export interface ReleasesApi {
  /** The org-wide feed, filtered and limited on the server. */
  getFeed(options: {
    limit: number;
    repo?: string;
    owner?: string;
    /** Bypass the server's cache; it single-flights the rebuild. */
    refresh?: boolean;
  }): Promise<ReleaseFeedResponse>;
  /**
   * One repository's releases. Only repos a catalog entity the viewer can
   * read points at are served; anything else is a 404.
   */
  getRepoReleases(
    slug: { owner: string; repo: string },
    options: { limit: number },
  ): Promise<RepoReleasesResponse>;
}

export const releasesApiRef = createApiRef<ReleasesApi>({
  id: 'app.github-releases',
});

/** Reads the github-releases backend plugin, found through discovery. */
export class GithubReleasesClient implements ReleasesApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  async getFeed(options: {
    limit: number;
    repo?: string;
    owner?: string;
    refresh?: boolean;
  }): Promise<ReleaseFeedResponse> {
    const params = new URLSearchParams({ limit: String(options.limit) });
    if (options.repo) params.set('repo', options.repo);
    if (options.owner) params.set('owner', options.owner);
    if (options.refresh) params.set('refresh', '1');
    return this.get(`releases?${params}`);
  }

  async getRepoReleases(
    { owner, repo }: { owner: string; repo: string },
    { limit }: { limit: number },
  ): Promise<RepoReleasesResponse> {
    return this.get(
      `releases/${encodeURIComponent(owner)}/${encodeURIComponent(
        repo,
      )}?limit=${limit}`,
    );
  }

  private async get<T>(path: string): Promise<T> {
    const baseUrl = await this.deps.discoveryApi.getBaseUrl('github-releases');
    const response = await this.deps.fetchApi.fetch(`${baseUrl}/${path}`);
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return (await response.json()) as T;
  }
}

/**
 * The message to show for a failed request: the server's own explanation when
 * Backstage's error middleware sent one (ResponseError keeps it as the cause),
 * else the error's message.
 */
export function releaseErrorMessage(error: Error): string {
  if (error instanceof ResponseError && error.cause?.message) {
    return error.cause.message;
  }
  return error.message;
}
